/**
 * Grid System
 * 
 * Responsibilities:
 * - Grid resolution computation
 * - Cell-to-pixel conversions
 * 
 * **Note:** Allocation is now handled by GridAllocator (see grid-allocator.ts)
 * This file only handles grid resolution computation.
 */

import { clamp } from '../utils/helpers.js';
import { computeGutters } from '../utils/viewport.js';
import type { GridState } from '../types/square.js';

/**
 * Grid cell count constraints
 * Prevents extreme zoom issues by limiting grid resolution
 * 
 * Increased limits to allow for better layouts like Eisenhower (2x2 grid needs substantial space)
 */
const GRID_CONSTRAINTS = {
  minCols: 1,
  maxCols: 12,  // Increased from 8 to allow more columns
  minRows: 1,
  maxRows: 8,   // Increased from 4 to allow more rows (Eisenhower needs at least 4 rows for 2x2 grid)
  minCellSize: 120,  // Reduced from 150 to allow more cells while maintaining reasonable size
} as const;

/**
 * Compute grid resolution based on viewport size
 * Calculates optimal column/row count and cell dimensions
 * 
 * FIRST PRINCIPLES APPROACH:
 * 1. Get viewport dimensions
 * 2. Compute gutters dynamically (accounting for logo/title footprint)
 * 3. Subtract gutters evenly (top, bottom, left, right) - symmetrical per axis
 * 4. That is the workspace
 * 5. Divide workspace evenly into uniform cells
 * 6. Ensure cells meet minimum size constraint
 * 7. Enforce grid cell count limits (8x4 max, 1x1 min)
 * 
 * CRITICAL: Returns computed values instead of mutating to prevent infinite loops.
 * Caller must batch all grid updates into a single state assignment.
 */
export function computeGridResolution(
  state: GridState,
  stageElement: HTMLElement | null,
  logoImage: HTMLImageElement | null = null,
  logoLoaded: boolean = false
): Partial<GridState> {
  if (!stageElement) {
    // Fallback: use viewport dimensions for gutter computation
    const gutters = computeGutters(
      logoImage,
      logoLoaded,
      window.innerWidth,
      window.innerHeight
    );
    return {
      gutterX: gutters.gutterX,
      gutterY: gutters.gutterY,
    };
  }

  const rect = stageElement.getBoundingClientRect();
  const viewportWidth = rect.width;
  const viewportHeight = rect.height;

  // Step 1: Compute gutters dynamically (accounting for logo/title footprint)
  // Returns symmetrical gutters (same on all sides of an axis)
  const gutters = computeGutters(
    logoImage,
    logoLoaded,
    viewportWidth,
    viewportHeight
  );
  const gutterX = gutters.gutterX;
  const gutterY = gutters.gutterY;

  // Step 2: Subtract gutters evenly (top, bottom, left, right) - symmetrical
  // That is the workspace
  const workspaceWidth = viewportWidth - 2 * gutterX;
  const workspaceHeight = viewportHeight - 2 * gutterY;

  // Step 3: Divide workspace evenly into uniform cells
  // We need to determine: how many columns/rows, and what cell size
  // Constraint: cells must be square and meet minimum size
  
  const minCell = GRID_CONSTRAINTS.minCellSize;
  const gap = state.gap;
  
  // Calculate maximum possible columns/rows based on minimum cell size
  // Formula: workspace = cols * cellSize + (cols - 1) * gap
  // Solving for cols: workspace = cols * (cellSize + gap) - gap
  // workspace + gap = cols * (cellSize + gap)
  // cols = (workspace + gap) / (cellSize + gap)
  let maxCols = Math.floor((workspaceWidth + gap) / (minCell + gap));
  let maxRows = Math.floor((workspaceHeight + gap) / (minCell + gap));
  
  // Step 4: Check if we can fit more columns/rows while still meeting minCellSize
  // This ensures we use available space efficiently (Dieter Rams: less but better)
  // Try adding one more column/row and see if the resulting cell size still meets minCellSize
  const testCols = maxCols + 1;
  const testCellWidth = (workspaceWidth + gap) / testCols - gap;
  if (testCellWidth >= minCell && testCols <= GRID_CONSTRAINTS.maxCols) {
    maxCols = testCols; // Use more columns if we can fit them
  }
  
  const testRows = maxRows + 1;
  const testCellHeight = (workspaceHeight + gap) / testRows - gap;
  if (testCellHeight >= minCell && testRows <= GRID_CONSTRAINTS.maxRows) {
    maxRows = testRows; // Use more rows if we can fit them
  }
  
  // Step 5: Enforce grid cell count limits (8x4 max, 1x1 min)
  // Prevents extreme zoom issues and maintains brand consistency
  const finalCols = clamp(Math.max(1, maxCols), GRID_CONSTRAINTS.minCols, GRID_CONSTRAINTS.maxCols);
  const finalRows = clamp(Math.max(1, maxRows), GRID_CONSTRAINTS.minRows, GRID_CONSTRAINTS.maxRows);
  
  // Step 6: Calculate exact cell size to fill workspace uniformly
  // Formula: workspace = cols * cellSize + (cols - 1) * gap
  // Solving for cellSize: workspace = cols * cellSize + cols * gap - gap
  // workspace = cols * (cellSize + gap) - gap
  // workspace + gap = cols * (cellSize + gap)
  // cellSize + gap = (workspace + gap) / cols
  // cellSize = (workspace + gap) / cols - gap
  
  // Calculate cell size for width and height
  const cellWidth = (workspaceWidth + gap) / finalCols - gap;
  const cellHeight = (workspaceHeight + gap) / finalRows - gap;
  
  // Make cells square by using the minimum (to ensure they fit)
  const cellSize = Math.min(cellWidth, cellHeight);
  
  // Verify: if cell size is too small, we have a logical conflict
  // This means the minimum cell size constraint makes it impossible
  // to create a uniform grid. In this case, we should reduce columns/rows.
  if (cellSize < minCell) {
    // Logical conflict: minimum cell size constraint prevents uniform grid
    // Reduce columns/rows until we can satisfy the constraint
    // This is an edge case we need to handle
    
    // Try reducing columns first
    let adjustedCols = finalCols;
    let adjustedCellW = cellWidth;
    while (adjustedCellW < minCell && adjustedCols > 1) {
      adjustedCols--;
      adjustedCellW = (workspaceWidth + gap) / adjustedCols - gap;
    }
    
    // Try reducing rows
    let adjustedRows = finalRows;
    let adjustedCellH = cellHeight;
    while (adjustedCellH < minCell && adjustedRows > 1) {
      adjustedRows--;
      adjustedCellH = (workspaceHeight + gap) / adjustedRows - gap;
    }
    
    // Use the configuration that gives us the largest square cells
    // while still meeting the minimum constraint
    const useAdjustedCols = adjustedCellW >= minCell;
    const useAdjustedRows = adjustedCellH >= minCell;
    
    if (useAdjustedCols && adjustedCellW >= minCell && adjustedCellW <= adjustedCellH) {
      // Use adjusted columns
      const newCols = clamp(adjustedCols, GRID_CONSTRAINTS.minCols, GRID_CONSTRAINTS.maxCols);
      const newCellSize = (workspaceWidth + gap) / newCols - gap;
      const newRows = clamp(
        Math.floor((workspaceHeight + gap) / (newCellSize + gap)),
        GRID_CONSTRAINTS.minRows,
        GRID_CONSTRAINTS.maxRows
      );
      const actualCellSize = Math.min(newCellSize, (workspaceHeight + gap) / newRows - gap);
      
      // Calculate actual grid dimensions
      const actualGridWidth = newCols * actualCellSize + (newCols - 1) * gap;
      const actualGridHeight = newRows * actualCellSize + (newRows - 1) * gap;
      
      // Center grid within workspace
      const finalGutterX = gutterX + (workspaceWidth - actualGridWidth) / 2;
      const finalGutterY = gutterY + (workspaceHeight - actualGridHeight) / 2;
      
      return {
        gutterX: finalGutterX,
        gutterY: finalGutterY,
        width: viewportWidth,
        height: viewportHeight,
        cols: newCols,
        rows: newRows,
        cellW: actualCellSize,
        cellH: actualCellSize,
      };
    } else if (useAdjustedRows && adjustedCellH >= minCell) {
      // Use adjusted rows
      const newRows = clamp(adjustedRows, GRID_CONSTRAINTS.minRows, GRID_CONSTRAINTS.maxRows);
      const newCellSize = (workspaceHeight + gap) / newRows - gap;
      const newCols = clamp(
        Math.floor((workspaceWidth + gap) / (newCellSize + gap)),
        GRID_CONSTRAINTS.minCols,
        GRID_CONSTRAINTS.maxCols
      );
      const actualCellSize = Math.min(newCellSize, (workspaceWidth + gap) / newCols - gap);
      
      // Calculate actual grid dimensions
      const actualGridWidth = newCols * actualCellSize + (newCols - 1) * gap;
      const actualGridHeight = newRows * actualCellSize + (newRows - 1) * gap;
      
      // Center grid within workspace
      const finalGutterX = gutterX + (workspaceWidth - actualGridWidth) / 2;
      const finalGutterY = gutterY + (workspaceHeight - actualGridHeight) / 2;
      
      return {
        gutterX: finalGutterX,
        gutterY: finalGutterY,
        width: viewportWidth,
        height: viewportHeight,
        cols: newCols,
        rows: newRows,
        cellW: actualCellSize,
        cellH: actualCellSize,
      };
    }
    
    // If we still can't satisfy the constraint, use minimum values
    // This is a logical conflict that should be reported
  }
  
  // Normal case: cell size meets minimum constraint
  // Calculate actual grid dimensions using the exact cell size
  const actualGridWidth = finalCols * cellSize + (finalCols - 1) * gap;
  const actualGridHeight = finalRows * cellSize + (finalRows - 1) * gap;
  
  // Center grid within workspace (if grid doesn't fill entire workspace)
  const finalGutterX = gutterX + (workspaceWidth - actualGridWidth) / 2;
  const finalGutterY = gutterY + (workspaceHeight - actualGridHeight) / 2;

  // Update grid info display
  const gridInfoEl = document.getElementById('gridInfo');
  if (gridInfoEl) {
    gridInfoEl.textContent = `${finalCols}×${finalRows} @ ${cellSize.toFixed(1)}×${cellSize.toFixed(1)}px`;
  }

  // Return computed values (caller will batch update state.grid)
  return {
    gutterX: finalGutterX,
    gutterY: finalGutterY,
    width: viewportWidth,
    height: viewportHeight,
    cols: finalCols,
    rows: finalRows,
    cellW: cellSize,
    cellH: cellSize,
  };
}
